import type { User } from "@employee-management/common";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiService } from "@/lib/api-service";
import { useState } from "react";

interface UserDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
}

export const UserDeleteDialog = ({
  open,
  onOpenChange,
  user,
}: UserDeleteDialogProps) => {
  const queryClient = useQueryClient();

  const [isActionPending, setIsActionPending] = useState(false);

  const handleDelete = () => {
    setIsActionPending(true);
    toast.promise(apiService.delete(`/users/${user.id}`), {
      loading: "Deleting user...",
      success: () => {
        queryClient.invalidateQueries({ queryKey: ["users"] });
        onOpenChange(false);
        return "User deleted successfully";
      },
      error: (error: Error) => {
        return error.message || "Failed to delete user";
      },
      finally: () => {
        setIsActionPending(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Delete User
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>{user.name}</strong>? This
            action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isActionPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isActionPending}
          >
            {isActionPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Delete User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
